<script setup lang="ts">
import DeleteButton from "@/components/DeleteButton.vue";
import { useEditing } from "@/composables/useEditing";
import type { User } from "@/users/types";
import { formatDatetime } from "@/utils/toFormattedDatetime";
import { EditPencil, Trash } from "@iconoir/vue";
import type { MutationStatus } from "@tanstack/vue-query";
import { computed, useTemplateRef } from "vue";
import type { Comment } from "./types";

const props = defineProps<{
  comment: Comment;
  deletingStatus: MutationStatus;
  me?: User;
}>();

const username = computed(() => {
  return props.comment.user.deletedAt ? "[gelöscht]" : props.comment.user.name;
});

const canDelete = computed(() => {
  return (
    props.me?.role === "Admin" ||
    props.me?.role === "Coordinator" ||
    props.me?.id === props.comment.user.id
  );
});

const canEdit = computed(() => {
  return props.me?.id === props.comment.user.id;
});

const emit = defineEmits<{
  initiateDelete: [];
  delete: [comment: Comment];
  update: [comment: Comment];
}>();

const { isEditing, draft, startEditing, cancelEditing, saveEditing } =
  useEditing(
    () => props.comment.content,
    (content: string) => emit("update", { ...props.comment, content }),
    useTemplateRef("editTextarea"),
  );
</script>

<template>
  <article>
    <header class="row">
      <small>
        {{ formatDatetime(comment.createdAt) }}
        | {{ username }}
      </small>
      <div v-if="!isEditing" class="row-self-space-auto row">
        <button
          v-if="canEdit"
          class="button button--tertiary"
          @click.prevent="startEditing"
        >
          <EditPencil /><span class="visually-hidden">bearbeiten</span>
        </button>
        <DeleteButton
          v-if="canDelete"
          class="button button--tertiary"
          :deleting-status="deletingStatus"
          @initiated="$emit('initiateDelete')"
          @confirmed="$emit('delete', comment)"
        >
          <Trash /><span class="visually-hidden">entfernen</span>
          <template #confirmation>
            Möchtest du den Kommentar von {{ comment.user.name }} wirklich
            löschen?
          </template>
        </DeleteButton>
      </div>
    </header>
    <div v-if="!isEditing" class="card">{{ comment.content }}</div>
    <form v-else class="column" @submit.prevent="saveEditing">
      <label :for="`edit-comment-${comment.id}`" class="visually-hidden"
        >Kommentar bearbeiten</label
      >
      <textarea
        :id="`edit-comment-${comment.id}`"
        ref="editTextarea"
        v-model="draft"
        rows="4"
      ></textarea>
      <div class="row row-end">
        <button class="button" type="submit">Speichern</button>
        <button
          class="button button--secondary"
          type="reset"
          @click="cancelEditing"
        >
          Abbrechen
        </button>
      </div>
    </form>
  </article>
</template>
